// src/services/api.js
import axios from 'axios';
import { API_BASE_URL } from '../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar o token automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com respostas e refresh token
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Funções da API
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login/', credentials),
  register: (userData) => {
    const publicApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return publicApi.post('/api/auth/register/', userData);
  },
  refreshToken: (refresh) => api.post('/api/auth/refresh/', { refresh }),
};

export const propertiesAPI = {
  list: (params) => api.get('/api/properties/', { params }),
  create: (propertyData) => {
    return api.post('/api/properties/', propertyData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  detail: (id) => api.get(`/api/properties/${id}/`),
  update: (id, propertyData) => {
    return api.put(`/api/properties/${id}/`, propertyData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id) => api.delete(`/api/properties/${id}/`),
  toggleFavorite: (id) => api.post(`/api/properties/${id}/favorite/`),
  
  // NOVO: Busca Inteligente
  smartSearch: (query, params = {}) => api.get('/api/properties/smart-search/', {
    params: { query, ...params }
  }),
  
  // NOVO: Sugestões em tempo real
  searchSuggestions: (query) => api.get('/api/properties/search-suggestions/', {
    params: { query }
  }),
  
  // NOVO: Imóveis em destaque (com boost ativo)
  featured: (params = {}) => api.get('/api/properties/featured/', { params }),
};

export const userAPI = {
  favorites: () => api.get('/api/user/favorites/'),
  properties: () => api.get('/api/user/properties/'),
  profile: () => api.get('/api/user/profile/'),
  getImportToken: () => api.get('/api/user/import-token/'),
  resetImportToken: () => api.post('/api/user/import-token/reset/'),
  uploadXML: (file, selectedIds = null) => {
    const fd = new FormData()
    fd.append('xml_file', file)
    if (selectedIds && selectedIds.length > 0) {
      selectedIds.forEach((id) => fd.append('selected_ids', id))
    }
    return api.post('/api/xml/upload/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
  },
  previewXML: (file) => {
    const fd = new FormData()
    fd.append('xml_file', file)
    return api.post('/api/xml/preview/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
};

export const adminAPI = {
  getSuperFeatured: () => api.get('/api/admin/super-featured/'),
  updateSuperFeatured: (featured) => api.post('/api/admin/super-featured/', { featured }),
  getPendingProperties: () => api.get('/api/admin/properties/pending/'),
  approveProperty: (id, action) => api.post(`/api/admin/properties/${id}/approve/`, { action }),
  getAllProperties: (params) => api.get('/api/admin/properties/', { params }),
  deleteProperty: (id) => api.delete(`/api/admin/properties/${id}/delete/`),

  // Gerenciar Usuários
  getUsers: (params) => api.get('/api/admin/users/', { params }),
  getUser: (id) => api.get(`/api/admin/users/${id}/`),
  updateUser: (id, data) => api.patch(`/api/admin/users/${id}/update/`, data),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}/delete/`),
};

export const reviewsAPI = {
  create: (reviewData) => api.post('/api/reviews/create/', reviewData),
  update: (reviewId, reviewData) => api.put(`/api/reviews/${reviewId}/`, reviewData),
  delete: (reviewId) => api.delete(`/api/reviews/${reviewId}/delete/`),
  respond: (reviewId, response) => api.post(`/api/reviews/${reviewId}/respond/`, { response }),
  markHelpful: (reviewId) => api.post(`/api/reviews/${reviewId}/helpful/`),
  canReview: (sellerId) => api.get(`/api/reviews/can-review/${sellerId}/`),
};

export default api;